/**
 * Centralized API service for Django REST Framework endpoints.
 *
 * This service provides a consistent interface for all DRF calls,
 * reusing the existing Axios instance (Vue.axios) which already has:
 * - baseURL set to VUE_APP_AUTH_HTTP (Django API)
 * - Bearer token auth via vue-auth
 * - 401 refresh interceptor via auth.axios.refresher.js
 *
 * Usage:
 *   import { api } from '@/services/api'
 *   const practices = await api.get('web.practice/')
 *   const assessment = await api.get('web.assessment/123/')
 *   await api.post('web.comment/', { comment: 'text', practice_id: 1 })
 *   await api.patch('web.indicatorresponse/456/', { compliance: 'MET' })
 */
import Vue from 'vue'
import camelcaseKeys from 'camelcase-keys'
import snakecaseKeys from 'snakecase-keys'

const defaultTransformOptions = { deep: true }

/**
 * Transform response data from snake_case to camelCase.
 * Handles single objects, arrays, and paginated responses.
 */
function transformResponse(data, options = defaultTransformOptions) {
  if (data == null) return data
  return camelcaseKeys(data, options)
}

/**
 * Transform request data from camelCase to snake_case.
 */
function transformRequest(data, options = defaultTransformOptions) {
  if (data == null) return data
  return snakecaseKeys(data, options)
}

class ApiService {
  /**
   * GET request with automatic camelCase transformation.
   * @param {string} url - Relative URL (resolved against Django API base)
   * @param {object} [params] - Query parameters (will be snake_cased)
   * @param {object} [options] - Additional axios config
   * @returns {Promise<any>} Transformed response data
   */
  async get(url, params = {}, options = {}) {
    const transformedParams = Object.keys(params).length
      ? transformRequest(params)
      : params

    const { data } = await Vue.axios.get(url, {
      params: transformedParams,
      ...options,
    })
    return transformResponse(data)
  }

  /**
   * POST request with automatic case transformation.
   * @param {string} url - Relative URL
   * @param {object} [body] - Request body (will be snake_cased)
   * @param {object} [options] - Additional axios config
   * @returns {Promise<any>} Transformed response data
   */
  async post(url, body = {}, options = {}) {
    const { data } = await Vue.axios.post(
      url,
      transformRequest(body),
      options,
    )
    return transformResponse(data)
  }

  /**
   * PUT request with automatic case transformation.
   * @param {string} url - Relative URL
   * @param {object} [body] - Request body (will be snake_cased)
   * @param {object} [options] - Additional axios config
   * @returns {Promise<any>} Transformed response data
   */
  async put(url, body = {}, options = {}) {
    const { data } = await Vue.axios.put(
      url,
      transformRequest(body),
      options,
    )
    return transformResponse(data)
  }

  /**
   * PATCH request with automatic case transformation.
   * @param {string} url - Relative URL
   * @param {object} [body] - Request body (will be snake_cased)
   * @param {object} [options] - Additional axios config
   * @returns {Promise<any>} Transformed response data
   */
  async patch(url, body = {}, options = {}) {
    const { data } = await Vue.axios.patch(
      url,
      transformRequest(body),
      options,
    )
    return transformResponse(data)
  }

  /**
   * DELETE request.
   * @param {string} url - Relative URL
   * @param {object} [options] - Additional axios config
   * @returns {Promise<any>} Transformed response data
   */
  async delete(url, options = {}) {
    const { data } = await Vue.axios.delete(url, options)
    return transformResponse(data)
  }

  /**
   * Raw GET without case transformation (for endpoints that already
   * return camelCase or where transformation would break the response).
   */
  async getRaw(url, params = {}, options = {}) {
    const { data } = await Vue.axios.get(url, { params, ...options })
    return data
  }

  /**
   * Raw POST without case transformation (for FormData uploads, etc).
   */
  async postRaw(url, body, options = {}) {
    const { data } = await Vue.axios.post(url, body, options)
    return data
  }
}

export const api = new ApiService()
